/**
 * The hand-off as a "who we've asked" fact (UX round 2026-10-13).
 *
 * An item sent on to someone went out by the hand-off email, and their reply
 * to it is recorded on the item (`latest_route_reply`). The rail's "Who
 * we've asked" panel only listed asks, so on QT-10003 it said "Nobody has
 * been asked" right beside John Kirk's reply. This turns the hand-off into
 * one row for that panel, and gives the Routed away list the reply's words.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import { isRoutedToUser } from '../../components/portal/threadTriageView';
import { handOffReply } from './respondedView';

export interface HandOffRow {
  /** Who the item is with, as the record names them. */
  to: string;
  /** When it was sent to them (ISO); null when not on record. */
  sentAt: string | null;
  /** Their reply to this send, with its first words; null when none. */
  reply: { who: string; at: string; excerpt: string } | null;
  /** Door gate (kanban 8a4d2212): the email has not gone out — it waits for
   *  Merlin's "already known?" check, or Merlin held it. Null once sent. */
  gate: 'pending' | 'held' | null;
}

/** The first `max` characters of a reply, on one line, cut at a word. */
export function shortExcerpt(text: string | null | undefined, max = 140): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:]+$/, '')}…`;
}

/**
 * The hand-off row for an item that is with someone else and still open;
 * null for the viewer's own items and closed ones.
 */
export function handOffRow(
  thread: Pick<DoorThreadRead, 'routed_to' | 'routed_at' | 'status'> &
    Partial<Pick<DoorThreadRead, 'latest_route_reply' | 'route_notified'>>,
  userName: string | null | undefined,
): HandOffRow | null {
  const to = (thread.routed_to ?? '').trim();
  if (!to || isRoutedToUser(thread, userName)) return null;
  if (thread.status === 'completed' || thread.status === 'dismissed') return null;
  const reply = handOffReply(thread);
  const gate =
    thread.route_notified === 'pending' || thread.route_notified === 'held' ? thread.route_notified : null;
  return {
    to,
    sentAt: thread.routed_at ?? null,
    gate,
    reply: reply
      ? { who: reply.who, at: reply.at, excerpt: shortExcerpt(thread.latest_route_reply?.excerpt) }
      : null,
  };
}
